/**
 * Shared Constants
 *
 * Common constants used across both frontend and backend
 * to ensure type consistency and single source of truth
 */

/**
 * Version status values
 *
 * Status flow:
 * - queued: File received, waiting in queue for processing
 * - processing: Queue handler actively processing the file
 * - completed: Successfully processed and stored
 * - failed: Processing failed (validation errors or system error)
 *
 * Note: 'pending' is deprecated but kept for backwards compatibility
 * with existing versions in flight.
 */
export const VERSION_STATUSES = ['queued', 'pending', 'processing', 'completed', 'failed'] as const;
export type VersionStatus = (typeof VERSION_STATUSES)[number];

/**
 * Version statuses that indicate processing is still in progress
 * Used to determine if an upload/processing operation is active
 */
export const VERSION_IN_PROGRESS_STATUSES: VersionStatus[] = ['queued', 'pending', 'processing'];

/**
 * Log levels for version logs
 */
export const LOG_LEVELS = ['info', 'warn', 'error', 'success'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

/**
 * Field source types (where a field originates from)
 */
export const FIELD_SOURCE_TYPES = ['base', 'relationship', 'calculated'] as const;
export type FieldSourceType = (typeof FIELD_SOURCE_TYPES)[number];

/**
 * Version selection options for linked tables
 */
export const VERSION_SELECTION_OPTIONS = ['current', 'previous'] as const;
export type VersionSelectionOption = (typeof VERSION_SELECTION_OPTIONS)[number];

/**
 * Target types for linked table relationships
 */
export const RELATIONSHIP_TARGET_TYPES = ['table', 'linked_table'] as const;
export type RelationshipTargetType = (typeof RELATIONSHIP_TARGET_TYPES)[number];

/**
 * Field description placeholders
 * Used to identify auto-generated descriptions that haven't been updated
 */
export const FIELD_DESCRIPTION_PLACEHOLDER_PREFIX = 'Extracted from';

/**
 * Helper function to check if a description is a placeholder (auto-generated)
 */
export function isPlaceholderDescription(description?: string | null): boolean {
  if (!description) return false;
  return description.startsWith(FIELD_DESCRIPTION_PLACEHOLDER_PREFIX);
}

/**
 * Helper function to generate a placeholder description
 */
export function createPlaceholderDescription(fileName: string): string {
  return `${FIELD_DESCRIPTION_PLACEHOLDER_PREFIX} ${fileName}`;
}

/**
 * Workspace roles (hierarchical)
 * Controls access levels at the workspace level
 *
 * Roles:
 * - viewer: Read-only access to workspace content
 * - editor: Can create and modify tables, versions, and linked tables
 * - owner: Full control including member management, billing, and deletion
 */
export const WORKSPACE_ROLES = ['viewer', 'editor', 'owner'] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

/**
 * Table roles (hierarchical)
 * Controls access levels at the table level
 */
export const TABLE_ROLES = ['reader', 'editor', 'manager'] as const;
export type TableRole = (typeof TABLE_ROLES)[number];

/**
 * Field data types supported across the application
 *
 * Types:
 * - text: General text data
 * - number: Numeric data (integers, decimals)
 * - boolean: True/false values
 * - date: Date only (YYYY-MM-DD)
 * - datetime: Date and time (ISO 8601)
 * - json: JSON objects/arrays
 * - email: Email addresses (validated format)
 * - url: URLs (validated format)
 */
export const FIELD_DATA_TYPES = [
  'text',
  'number',
  'boolean',
  'date',
  'datetime',
  'json',
  'email',
  'url',
] as const;

export type FieldDataType = (typeof FIELD_DATA_TYPES)[number];

/**
 * Display labels for field data types
 * Maps FieldDataType values to human-readable labels
 */
export const FIELD_DATA_TYPE_LABELS: Record<FieldDataType, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  date: 'Date',
  datetime: 'DateTime',
  json: 'JSON',
  email: 'Email',
  url: 'URL',
} as const;

/**
 * Mapping from legacy/inferred data types to canonical FieldDataType
 * Used when importing data from external sources or older systems
 */
const LEGACY_TYPE_MAP: Record<string, FieldDataType> = {
  varchar: 'text',
  string: 'text',
  integer: 'number',
  int: 'number',
  bigint: 'number',
  float: 'number',
  double: 'number',
  decimal: 'number',
  bool: 'boolean',
  timestamp: 'datetime',
  time: 'datetime',
};

/**
 * Normalize a data type to canonical FieldDataType
 * Converts legacy/inferred types to standard values
 *
 * @param dataType - The data type to normalize (may be legacy format)
 * @returns Canonical FieldDataType value
 */
export function normalizeDataType(dataType: string | undefined): FieldDataType {
  if (!dataType) return 'text';

  // Already canonical
  if (FIELD_DATA_TYPES.includes(dataType as FieldDataType)) {
    return dataType as FieldDataType;
  }

  // Map legacy types to canonical
  const normalized = dataType.toLowerCase();
  return LEGACY_TYPE_MAP[normalized] || 'text';
}

/**
 * Format data type for display
 * Converts both canonical and legacy types to human-readable labels
 *
 * @param dataType - The data type to format
 * @returns Human-readable label
 */
export function formatDataType(dataType: string | undefined): string {
  if (!dataType) return 'Unknown';

  // Check canonical types first
  if (dataType in FIELD_DATA_TYPE_LABELS) {
    return FIELD_DATA_TYPE_LABELS[dataType as FieldDataType];
  }

  // Normalize and return label
  const normalized = normalizeDataType(dataType);
  return FIELD_DATA_TYPE_LABELS[normalized];
}

/**
 * Data types supported for calculated columns
 *
 * Subset of FIELD_DATA_TYPES that excludes complex types (json, email, url)
 * because calculated columns produce simple output types.
 */
export const CALCULATED_COLUMN_DATA_TYPES = [
  'text',
  'number',
  'boolean',
  'date',
  'datetime',
] as const;

export type CalculatedColumnDataType = (typeof CALCULATED_COLUMN_DATA_TYPES)[number];

// ============================================================================
// FILTER OPERATORS
// ============================================================================

/**
 * All supported filter operators (snake_case - canonical format)
 *
 * Used for:
 * - Linked table filter configuration
 * - Query building
 * - API validation
 *
 * Operators:
 * - Equality: equals, not_equals
 * - String: contains, not_contains, starts_with, ends_with
 * - Numeric: greater_than, less_than, greater_than_or_equal, less_than_or_equal
 * - Set: in, not_in
 * - Null: is_null, is_not_null
 */
export const FILTER_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'less_than',
  'greater_than_or_equal',
  'less_than_or_equal',
  'in',
  'not_in',
  'is_null',
  'is_not_null',
] as const;

export type FilterOperator = (typeof FILTER_OPERATORS)[number];

/**
 * Data grid filter operators (camelCase format for frontend compatibility)
 *
 * These are the same operators as FILTER_OPERATORS but in camelCase format
 * for use with data grid components that expect camelCase naming.
 */
export const DATA_GRID_FILTER_OPERATORS = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'startsWith',
  'endsWith',
  'greaterThan',
  'lessThan',
  'greaterThanOrEqual',
  'lessThanOrEqual',
  'in',
  'notIn',
  'isNull',
  'isNotNull',
] as const;

export type DataGridFilterOperator = (typeof DATA_GRID_FILTER_OPERATORS)[number];

/**
 * Mapping from snake_case to camelCase filter operators
 */
export const FILTER_OPERATOR_TO_CAMEL: Record<FilterOperator, DataGridFilterOperator> = {
  equals: 'equals',
  not_equals: 'notEquals',
  contains: 'contains',
  not_contains: 'notContains',
  starts_with: 'startsWith',
  ends_with: 'endsWith',
  greater_than: 'greaterThan',
  less_than: 'lessThan',
  greater_than_or_equal: 'greaterThanOrEqual',
  less_than_or_equal: 'lessThanOrEqual',
  in: 'in',
  not_in: 'notIn',
  is_null: 'isNull',
  is_not_null: 'isNotNull',
};

/**
 * Mapping from camelCase to snake_case filter operators
 */
export const FILTER_OPERATOR_TO_SNAKE: Record<DataGridFilterOperator, FilterOperator> = {
  equals: 'equals',
  notEquals: 'not_equals',
  contains: 'contains',
  notContains: 'not_contains',
  startsWith: 'starts_with',
  endsWith: 'ends_with',
  greaterThan: 'greater_than',
  lessThan: 'less_than',
  greaterThanOrEqual: 'greater_than_or_equal',
  lessThanOrEqual: 'less_than_or_equal',
  in: 'in',
  notIn: 'not_in',
  isNull: 'is_null',
  isNotNull: 'is_not_null',
};

/**
 * Filter operators available for each field data type (snake_case)
 *
 * Text-like types support string operations (contains, in)
 * Numeric/date types support comparison operations (>, <, >=, <=)
 * Boolean only supports equality checks
 * JSON only supports null checks (complex structure)
 */
export const FILTER_OPERATORS_BY_TYPE: Record<FieldDataType, FilterOperator[]> = {
  text: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_null', 'is_not_null'],
  email: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_null', 'is_not_null'],
  url: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_null', 'is_not_null'],
  number: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'in', 'not_in', 'is_null', 'is_not_null'],
  date: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_null', 'is_not_null'],
  datetime: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_null', 'is_not_null'],
  boolean: ['equals', 'not_equals', 'is_null', 'is_not_null'],
  json: ['is_null', 'is_not_null'],
};

/**
 * System-reserved version tags
 * Used for version lifecycle management and identifying specific versions
 *
 * System tags are distinct from user-assigned custom tags:
 * - system_tag: Single system-assigned status ('current', 'previous', or null)
 *   Stored in the versions.system_tag column
 * - tags: Multiple user-assigned custom tags (workspace-level organizational entities)
 *   Stored via the version_tags junction table
 *
 * System tags:
 * - current: Current/active version (most recent production version)
 * - previous: Previous version (for rollback and comparison)
 */
export const SYSTEM_TAGS = ['current', 'previous'] as const;

export type SystemTag = (typeof SYSTEM_TAGS)[number];

// ============================================================================
// SYSTEM TAG UTILITIES
// ============================================================================

/**
 * Check if a tag value is a system-reserved tag
 */
export function isSystemTag(tag: string): tag is SystemTag {
  return (SYSTEM_TAGS as readonly string[]).includes(tag);
}

// ============================================================================
// EXCEL PLUGIN CONSTANTS
// ============================================================================

/**
 * Token name used for Excel Plugin API tokens
 * Ensures consistent naming across token creation and revocation
 */
export const EXCEL_PLUGIN_TOKEN_NAME = 'Excel Plugin';

/**
 * Device code validation constraints
 * Used for OAuth device code flow validation
 */
export const DEVICE_CODE_MIN_LENGTH = 16;
export const DEVICE_CODE_MAX_LENGTH = 64;

/**
 * Device code expiration in minutes
 */
export const DEVICE_CODE_EXPIRY_MINUTES = 10;

/**
 * Token expiration in days for Excel plugin tokens
 */
export const EXCEL_TOKEN_EXPIRY_DAYS = 30;

/**
 * Minimum polling interval in seconds for device code flow
 */
export const DEVICE_CODE_POLL_INTERVAL_SECONDS = 2;

// ============================================================================
// JOIN TYPES
// ============================================================================

/**
 * SQL join types for linked table relationships
 */
export const JOIN_TYPES = ['LEFT', 'INNER', 'RIGHT', 'FULL'] as const;
export type JoinType = (typeof JOIN_TYPES)[number];
